import React, { useEffect, useRef } from "react";
import {
  Box,
  CircularProgress,
  IconButton,
  Table,
  TableRow,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  KeyboardDoubleArrowLeft,
  KeyboardDoubleArrowRight,
} from "@mui/icons-material";
import {
  PagedUserModel,
  UserModel,
} from "../../../../main/users-facility/api/types";
import {
  pagination,
  paginationItem,
  paginationTotal,
  StyledTableBody,
  StyledTableCell,
  StyledTableContainer,
  StyledTableHeader,
  StyledTableRow,
} from "../../table";
import {
  customStyledTableRow,
  dataTable,
  emptyCont,
  userEmailCell,
  userNameCell,
  userPhotoCell,
} from "../sxprops";
import { imageCell, paginationCont } from "../../../../main/products/sxprops";
import useUserInvStore from "../api/store";

interface IDataTable {
  isFetching: boolean;
  data: PagedUserModel | undefined;
  pageNumber: number;
  pageSize: number;
  selectedUser: UserModel | undefined;
  setSelectedUser: (val: UserModel | undefined) => void;
  setPageNumber: (val: number) => void;
}

export const DataTable: React.FC<IDataTable> = ({
  isFetching,
  data,
  pageNumber,
  pageSize,
  selectedUser,
  setSelectedUser,
  setPageNumber,
}) => {
  //pageNumber, pageSize, selectedUser, setSelectedUser, setPageNumberconst { pageNumber, pageSize, selectedUser, setSelectedUser, setPageNumber } = useUserInvitationStore();
  const totalPages = Math.ceil((data?.totalItems || 0) / pageSize);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const theme = useTheme();
  const isTabletUp = useMediaQuery(theme.breakpoints.up("tablet"));
  const userInvStore = useUserInvStore();

  const focusPage = () => {
    itemRefs.current[pageNumber - 1]?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "center",
    });
  };

  const handleSelectuser = (user: UserModel) => {
    setSelectedUser(user);
  };

  useEffect(() => {
    if (data?.items !== undefined && data.items.length === 0 && !isFetching) {
      console.log("SKIP");
    }
  }, [data, isFetching]);

  return (
    <Box sx={dataTable.main}>
      {userInvStore.clearingUserForm && (
        <Box sx={dataTable.progressIndicatorCont}>
          <CircularProgress />
        </Box>
      )}
      <StyledTableContainer sx={{ height: "300px" }}>
        <Table>
          <StyledTableHeader>
            <TableRow>
              <StyledTableCell align="center" colSpan={2}>
                Full Name
              </StyledTableCell>
              <StyledTableCell align="center">Email</StyledTableCell>
            </TableRow>
          </StyledTableHeader>

          <StyledTableBody>
            {isFetching && (
              <StyledTableRow>
                <StyledTableCell
                  sx={customStyledTableRow}
                  align="center"
                  colSpan={5}
                  rowSpan={5}
                >
                  <Box sx={emptyCont}>
                    <CircularProgress />
                  </Box>
                </StyledTableCell>
              </StyledTableRow>
            )}

            {!isFetching && data?.totalItems === 0 && (
              <StyledTableRow>
                <StyledTableCell
                  data-testid="user-invite-select-empty-state"
                  sx={dataTable.emptyResult.cell}
                  align="center"
                  colSpan={5}
                >
                  <Box sx={emptyCont}>
                    <Box sx={dataTable.emptyResult.main}>
                      <img
                        src="/empty-table-data-logo.svg"
                        alt="No Data"
                        style={{ maxHeight: 300 }}
                      />
                      <Box component="span" sx={dataTable.emptyResult.text}>
                        No results found.
                      </Box>
                    </Box>
                  </Box>
                </StyledTableCell>
              </StyledTableRow>
            )}

            {!isFetching &&
              data?.items.map((row, rowIndex) => {
                const isActive = selectedUser === row;
                return (
                  <StyledTableRow
                    id={`user-row-${row.id}`}
                    data-testid={`user-row-${row.id}`}
                    ref={
                      rowIndex === 0
                        ? userInvStore.inviteUserFirstResultButtonRef
                        : undefined
                    }
                    key={row.id}
                    onClick={() => {
                      handleSelectuser(row);
                    }}
                  >
                    <StyledTableCell
                      align="center"
                      sx={userPhotoCell(isActive)}
                    >
                      <Box
                        sx={{
                          ...imageCell,
                          width: "30px",
                          height: "30px",
                        }}
                      >
                        <img
                          src={row.photoUrl || "/bizlogo.svg"}
                          alt="Bizbox logo"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      </Box>
                    </StyledTableCell>

                    <StyledTableCell
                      align="left"
                      title={row.fullName}
                      sx={userNameCell(isActive)}
                    >
                      {row.fullName}
                    </StyledTableCell>

                    <StyledTableCell
                      align="left"
                      title={row.email}
                      sx={userEmailCell(isActive)}
                    >
                      {row.email}
                    </StyledTableCell>
                  </StyledTableRow>
                );
              })}
          </StyledTableBody>
        </Table>
      </StyledTableContainer>
      <Box sx={paginationCont}>
        <IconButton
          id="user-invite-select-pagination-prev"
          data-testid="user-invite-select-pagination-prev"
          sx={{
            color: "primary.main",
            opacity: pageNumber > 1 && (data?.totalItems || 0) > 0 ? 1 : 0,
          }}
          onClick={() => {
            if (pageNumber > 1) {
              setPageNumber(pageNumber - 1);
              focusPage();
            }
          }}
        >
          <KeyboardDoubleArrowLeft />
        </IconButton>

        <Box sx={pagination}>
          {Array.from({ length: totalPages }).map((_, i) => {
            const value = i + 1;
            const isSelected: boolean = pageNumber === value;
            return (
              <Box
                ref={(el: HTMLDivElement | null) => (itemRefs.current[i] = el)}
                onClick={() => {
                  setPageNumber(value);
                }}
                key={i}
                id={`user-invite-select-pagination-page-${value}`}
                data-testid={`user-invite-select-pagination-page-${value}`}
                sx={paginationItem(isSelected, theme)}
              >
                {value}
              </Box>
            );
          })}
        </Box>

        <IconButton
          id="user-invite-select-pagination-next"
          data-testid="user-invite-select-pagination-next"
          sx={{
            color: "primary.main",
            opacity: pageNumber < totalPages ? 1 : 0,
          }}
          onClick={() => {
            if (pageNumber < totalPages) {
              setPageNumber(pageNumber + 1);
              focusPage();
            }
          }}
        >
          <KeyboardDoubleArrowRight />
        </IconButton>

        <Box sx={paginationTotal}>
          {isTabletUp ? "Total Record(s)" : "Total"}: {data?.totalItems || 0}
        </Box>
      </Box>
    </Box>
  );
};
